import { faPenToSquare } from "@fortawesome/pro-duotone-svg-icons";
import { faAngleDoubleLeft, faAngleDoubleRight, faAngleLeft, faAngleRight, faMagnifyingGlass, faPlus } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AutoComplete, Button, Card, Select, Skeleton, Spin, Table } from "antd";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ForwarderService from "../services/Forwarder/ForwarderService";
import NewForwarder from "./NewForwarder";
import AppButton from "../common/AppButton";

const { Option } = Select;

function ForwarderList() {
  const navigate = useNavigate();
  const location = useLocation();
  const [dataSource, setDataSource] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const [searchInput, setSearchInput] = useState(location.state?.searchInput || "");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedForwarder, setSelectedForwarder] = useState(
    location.state?.searchInput || null
  );

  const [pagination, setPagination] = useState({ size: 50, num: 0 });
  const [sorter, setSorter] = useState({ field: "", order: "" });

  const fetchForwarder = async (params = {}) => {
    setIsLoading(true);
    try {
      const request = {
        companyShortName: selectedForwarder || null,
        pageFilter: { all: false, size: pagination.size, num: pagination.num },
        sortFilter: { field: sorter.field, order: sorter.order },
        ...params,
      };
      const response = await ForwarderService.getAllForwarder(request);
      if (response.status === 200) setDataSource(response.data);
    } catch (err) {
      console.error("Error fetching forwarders:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchForwarder();
  }, [selectedForwarder, pagination, sorter]);

  useEffect(() => {
    if (!searchInput) {
      setSearchResults([]);
      return;
    }
    const delay = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const response = await ForwarderService.getForwarderDropDown(searchInput);
        setSearchResults(response.status === 200 ? response.data : []);
      } catch (err) {
        console.error("Error fetching search results:", err);
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 600);
    return () => clearTimeout(delay);
  }, [searchInput]);

  const handleSearchInputChange = (val) => setSearchInput(val);
  const handleForwarderSelect = (val) => setSelectedForwarder(val);
  const handleClearSearch = () => {
    setSearchInput("");
    setSearchResults([]);
    setSelectedForwarder(null);
  };

  const handleView = (record) => {
    navigate("/forwarder-details", {
      state: { pathname: "/settings/freight-forwarder", pageMode: "edit", forwarderID: record.id, searchInput: selectedForwarder || "" },
    });
  };

  const handleTableChange = (paginationInfo, _, sort) => {
    setPagination({
      size: paginationInfo.pageSize,
      num: paginationInfo.current - 1,
    });
    setSorter({
      field: sort.field || "",
      order:
        sort.order === "ascend"
          ? "asc"
          : sort.order === "descend"
            ? "desc"
            : "",
    });
  };

  const renderPagination = () => {
    const current = pagination.num + 1;
    const total = dataSource?.total ?? 0;
    const totalPages = Math.ceil(total / pagination.size);

    return (
      <div className="flex justify-between items-center mt-2 px-2">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Rows per page:</span>
          <Select
            value={String(pagination.size)}
            onChange={(val) => setPagination({ num: 0, size: Number(val) })}
            style={{ width: 80 }}
          >
            {[10, 20, 50, 100].map((n) => (
              <Option key={n} value={String(n)}>
                {n}
              </Option>
            ))}
          </Select>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-600">
            {`${(current - 1) * pagination.size + 1}-${Math.min(
              current * pagination.size,
              total
            )} of ${total} items`}
          </span>
          <div className="flex gap-2">
            <Button onClick={() => setPagination({ ...pagination, num: 0 })} disabled={current === 1}>
              <FontAwesomeIcon icon={faAngleDoubleLeft} />
            </Button>
            <Button
              onClick={() => setPagination({ ...pagination, num: pagination.num - 1 })}
              disabled={current === 1}
            >
              <FontAwesomeIcon icon={faAngleLeft} />
            </Button>
            <Button
              onClick={() => setPagination({ ...pagination, num: pagination.num + 1 })}
              disabled={current === totalPages}
            >
              <FontAwesomeIcon icon={faAngleRight} />
            </Button>
            <Button
              onClick={() =>
                setPagination({ ...pagination, num: totalPages - 1 })
              }
              disabled={current === totalPages}
            >
              <FontAwesomeIcon icon={faAngleDoubleRight} />
            </Button>
          </div>
        </div>
      </div>
    );
  };

  const columns = [
    {
      title: "",
      key: "action",
      align: "left",
      render: (record) => (
        <Button type="link" onClick={() => handleView(record)}>
          <FontAwesomeIcon
            icon={faPenToSquare}
            className="text-xl text-black p-2 hover:text-cyan-500"
          />
        </Button>
      ),
    },
    {
      title: "Company Name",
      dataIndex: "companyName",
      key: "companyName",
      align: "left",
      sorter: true,
      render: (text) => (isLoading ? <Skeleton active size="small" /> : text),
    },
    {
      title: "Short Code",
      dataIndex: "companyShortName",
      key: "companyShortName",
      align: "left",
      sorter: true,
      width: 200,
      render: (text) => (isLoading ? <Skeleton active size="small" /> : text),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      align: "left",
      render: (text) => (isLoading ? <Skeleton active size="small" /> : text),
    },
    {
      title: "Contact Name",
      dataIndex: "contactName",
      key: "contactName",
      align: "left",
      render: (text) => (isLoading ? <Skeleton active size="small" /> : text),
    },
    {
      title: "Free Days",
      dataIndex: "detentionFreeDays",
      key: "detentionFreeDays",
      align: "left",
      width: 150,
      render: (text) => (isLoading ? <Skeleton active size="small" /> : text),
    },
    {
      title: "Daily Fee",
      dataIndex: "detentionDailyFee",
      key: "detentionDailyFee",
      align: "left",
      width: 150,
      render: (text) => (isLoading ? <Skeleton active size="small" /> : text),
    },
  ];

  return (
    <div className="px-8 mb-4">
      <div className="flex justify-between items-end w-full my-2 px-2">
        <span
          className="!font-medium text-[30px] !text-[#015373] !m-0 pl-4 mt-10 font-inter"
        >
          Freight Forwarder
        </span>
        <div className="flex justify-around gap-2 mr-8">
          <AutoComplete
            className="border w-72 font-inter !bg-gray-300"
            placeholder="Search forwarder"
            prefix={
              <FontAwesomeIcon
                icon={faMagnifyingGlass}
                className="opacity-70 mr-2"
              />
            }
            options={searchResults.map((item) => ({
              value: item.companyShortName,
              label: item.companyName,
              item,
            }))}
            value={searchInput}
            onChange={handleSearchInputChange}
            onSelect={handleForwarderSelect}
            allowClear
            onClear={handleClearSearch}
            notFoundContent={
              searchLoading ? <Spin size="default" /> : "No results found"
            }
          />
          <AppButton variant="new" onClick={() => navigate("/forwarder-details", { state: { pathname: "/settings/freight-forwarder", pageMode: "new" } })}>
            New
          </AppButton>
        </div>
      </div>

      <Card className="rounded-3xl shadow-xl bg-white bg-opacity-80">
        <Table
          size="middle"
          className="mx-0 mt-4 border-2 border-gray-300 rounded-lg overflow-x-auto"
          columns={columns}
          dataSource={
            isLoading
              ? new Array(10).fill(null).map((_, index) => ({ key: index }))
              : dataSource?.data?.map((item, index) => ({ ...item, key: index }))
          }
          pagination={false}
          onChange={handleTableChange}
          bordered={false}
          scroll={{ x: "max-content", y: "calc(100vh - 270px)" }}
          rowClassName={(_, index) =>
            index % 2 === 0 ? "text-sm" : "text-sm bg-[#f3f3f3]"
          }

        />
        {renderPagination()}
        <NewForwarder
          visible={isModalVisible}
          onClose={() => setIsModalVisible(false)}
          onSuccess={() => fetchForwarder()}
        />
      </Card>
    </div>
  );
}

export default ForwarderList;
